// src/pages/Auth.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { checkEmailExist } from '@/lib/supabase';
import { withTimeout } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import EmailVerification from '../EmailVerification'; // Import the new component
// console.log("🟨🟩🟪🟥🟦🟫⬛️ Loading <pages/Auth>")


const Auth = () => {
  const { loading: authLoading, session } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const [handlingOAuthCallback, setHandlingOAuthCallback] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();


  useEffect(() => {
    // OAuth 回调判断：Supabase v2 使用 code
    const isOAuth =
      window.location.search.includes('code=') ||
      window.location.hash.includes('access_token=');

    if (isOAuth) {
      console.log('>>> OAuth callback detected');
      setIsOAuthCallback(true);
    }
  }, []);

  /**
   * ============================
   * OAuth callback handler
   * ============================
   * 当从 Google / Facebook 跳回
   * /#access_token=...
   * 时，这个 effect 会自动执行
   */
  useEffect(() => {
    if (!window.location.hash) return;
    console.log('>>> OAuth callback handler effected');

    setHandlingOAuthCallback(true);

    const handleOAuthCallback = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('OAuth callback error:', error);
        toast({
          title: 'Sign in failed',
          description: 'OAuth callback failed, please try again.',
          variant: 'destructive',
        });
        setHandlingOAuthCallback(false);
        return;
      }

      if (data.session) {
        console.log('>>> OAuth session established');
        // 不在这里 navigate
        // 让 useAuth + profile useEffect 统一处理跳转
        setHandlingOAuthCallback(false);
      }
    };

    handleOAuthCallback();
  }, [toast]);

  // Detect incognito mode and notify customer
  // In incognito mode write to local storage might be acting wired
  // Supabase auth no stable
  // Below detection is not stable
  // useEffect(() => {
  //   if ('storage' in navigator && 'persisted' in navigator.storage) {
  //     navigator.storage.persisted().then(persisted => {
  //       if (!persisted) {
  //         toast({
  //           title: 'Incognito mode detected',
  //           description: 'Please use a normal browser window to sign in.',
  //           variant: 'destructive',
  //         });
  //       }
  //     }).catch(() => {
  //       // 某些浏览器不支持或会抛异常，直接忽略
  //     });
  //   }
  // }, []);


  /**
   * =========================================
   * Auto redirect when auth + profile ready
   * =========================================
   */
  useEffect(() => {
    // Supabase 还在初始化
    if (authLoading) return;

    // 是否登录
    if (session) {
      navigate('/', { replace: true });
    }
  }, [authLoading, session, navigate]);

  /**
   * ============================
   * Global loading states
   * ============================
   */
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await withTimeout(
        supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              role: 'student',
              region: 'GLOBAL',
            },
            emailRedirectTo: `${window.location.origin}/auth`,
          },
        }),
        10000
      );

      if (error) throw error;

      if (data.user && !data.user.email_confirmed_at) {
        setShowEmailVerification(true);
        toast({
          title: "Registration successful!",
          description: "Please check your email to verify your account.",
        });
      } else {
        toast({
          title: "Registration successful!",
          description: "Enjoy your creative journey.",
        });
      }
    } catch (err) {
      toast({
        title: "Registration failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Step 1: 先检查邮箱是否注册过
      console.log('>>> Checking email existance ...');

      const emailExists = await withTimeout(
        checkEmailExist(email),
        5000
      );

      if (!emailExists) {
        // 明确是「未注册」
        toast({
          title: 'Account not found',
          description: 'This email is not registered. Please sign up first.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Step 2: 邮箱存在，再尝试登录 → 此时错误一定是密码错了
      console.log('>>> Email exist, now checking password correctness...');

      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        10000
      );

      if (error) {
        toast({
          title: 'Incorrect email or password',
          description: 'Please check your email or password and try again.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      console.log('>>> Auth check passed');

      // 记录 region
      localStorage.setItem('region', 'GLOBAL');

      // 登录成功
      toast({
        title: "Sign in suceessfully",
        description: "Welcome back!",
      });
      // No manual navigation → let useEffect handle it
    } catch (err) {
      console.log('Sign in failed:', err);
      toast({
        title: "Sign in failed",
        description: "Connection is unstable, try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}`,
        },
      });
      if (error) throw error;
    } catch (err) {
      toast({
        title: "Google sign in failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleMicrosoftAuth = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email',
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
    } catch (err) {
      toast({
        title: 'Microsoft sign in failed',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // const handleFacebookAuth = async () => {
  //   try {
  //     const { error } = await supabase.auth.signInWithOAuth({
  //       provider: 'facebook',
  //       options: {
  //         redirectTo: `${window.location.origin}/`,
  //       },
  //     });
  //     if (error) throw error;
  //   } catch (err) {
  //     toast({
  //       title: "Facebook sign in failed",
  //       description: err,
  //       variant: "destructive",
  //     });
  //   }
  // };

  if (showEmailVerification) {
    return <EmailVerification email={email} />;
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center transition-all ${
        isOAuthCallback ? 'blur-sm pointer-events-none select-none' : ''
      }`}
    >
      {/* 正在登陆的显示层 */}
      {isOAuthCallback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
          <div className="flex flex-col items-center space-y-6 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="space-y-2">
              <p className="text-lg font-medium">Signing you in</p>
              <p className="text-sm text-muted-foreground">
                We’re securely completing your login…
              </p>
            </div>
          </div>
        </div>
      )}

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-primary">Welcome</CardTitle>
          <CardDescription>
            Join our art education platform to start your creative journey
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 1. 3rd party auth: Google & Facebook */}
          <div className="space-y-4">  {/* 每个按钮之间加大间距 */}
            <Button
              variant="outline"
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full h-12 rounded-xl border-2 hover:border-primary/50 transition-all"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <Button
              variant="outline"
              onClick={handleMicrosoftAuth}
              disabled={loading}
              className="w-full h-12 rounded-xl border-2 hover:border-primary/50 transition-all"
            >
              <svg
                className="mr-3 h-5 w-5"
                viewBox="0 0 23 23"
                aria-hidden="true"
              >
                <rect width="10.8" height="10.8" fill="#F25022" />
                <rect x="12.2" width="10.8" height="10.8" fill="#7FBA00" />
                <rect y="12.2" width="10.8" height="10.8" fill="#00A4EF" />
                <rect x="12.2" y="12.2" width="10.8" height="10.8" fill="#FFB900" />
              </svg>

              Continue with Microsoft
            </Button>

            {/* <Button
              variant="outline"
              onClick={handleFacebookAuth}
              disabled={loading}
              className="w-full h-12 rounded-xl border-2 hover:border-primary/50 transition-all"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </Button> */}
          </div>

          {/* 2. “Or continue with” 与下方内容加大间距 */}
          <div className="relative py-8">  {/* 重点：py-8 加大上下间距 */}
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="space-y-4">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Sign In
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    minLength={8}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Sign Up
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;